import {
  BufferAttribute,
  BufferGeometry,
  Mesh,
  MeshStandardNodeMaterial,
  RepeatWrapping,
  SRGBColorSpace,
  TextureLoader,
  type Texture,
} from 'three/webgpu';
import {
  attribute,
  color,
  float,
  mix,
  mx_noise_float,
  normalWorld,
  positionView,
  positionWorld,
  smoothstep,
  texture,
  uniform,
  vertexColor,
} from 'three/tsl';
import type { Heightfield } from '@stillsun/shared';
import { renderFlags } from './renderer.js';

/**
 * TSL's published types infer an unconstrained type parameter from `attribute(name, 'vec4')`
 * and widen it to `string`, which loses every swizzle on the result. Same escape hatch
 * render/decals.ts and render/sky.ts use, kept to the one node that needs it.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyNode = any;

// ---------------------------------------------------------------------------------------------
// The four splat layers.
//
// One material, one draw, four albedos blended by a per-vertex weight vector. This replaces the
// old regolith stack — five `mx_noise_float` octaves, a two-octave texture sample with a distance
// mip bias, and a surface-gradient ripple normal, all per fragment over the whole sheet — which
// existed to make a *single* material read as varied ground. A splat says what the ground is
// instead of pretending, costs four multiply-adds, and is what a 500 m zone needs.
//
// The weights are painted on the CPU into a `splat` vec4 attribute (grass, dirt, rock, sand) and
// interpolated for free by the rasteriser: no texture fetch in the fragment stage at all, which
// is cheaper than a splat DataTexture and cannot disagree with the sim grid it was painted from.
// The name is `splat`, not `color`: `color` is region.ts's zone tint and both can be present.
// ---------------------------------------------------------------------------------------------

/** Layer albedos. Kept inside the Stillsun palette: nothing here is a saturated garden green. */
const LAYER_GRASS = 0x4e5c33;
const LAYER_DIRT = 0x4a3b2c;
const LAYER_ROCK = 0x5c5850;
const LAYER_SAND = 0x8a7a5e;
/** Per-layer roughness, blended by the same weights. Sand is powder; rock is scoured. */
const ROUGH_GRASS = 0.94;
const ROUGH_DIRT = 0.9;
const ROUGH_ROCK = 0.72;
const ROUGH_SAND = 0.98;

/** Wavelength of the low-frequency mask that breaks up the height/slope bands, in metres. */
const SPLAT_NOISE_M = 38;
/** Slope (1 - n.y) over which ground turns to bare rock. */
const ROCK_SLOPE_LO = 0.18;
const ROCK_SLOPE_HI = 0.55;
/** Normalised height (0 = the terrain's lowest point, 1 = its highest) of the sand basin. */
const SAND_H_LO = 0.06;
const SAND_H_HI = 0.34;
/** Normalised height above which grass gives out and the ground goes back to dirt and rock. */
const GRASS_H_HI = 0.78;

/** `smoothstep`, on the CPU, matching the TSL/GLSL definition exactly. */
function sstep(edge0: number, edge1: number, x: number): number {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** Deterministic 2D value hash in 0..1. Stable across reloads and between client and tools. */
function hash2(ix: number, iz: number): number {
  const s = Math.sin(ix * 127.1 + iz * 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/** Smooth value noise in 0..1 with a `SPLAT_NOISE_M` period. One octave: this only has to
 *  break up the banding of the height and slope terms, not carry detail. */
function lowNoise(x: number, z: number): number {
  const px = x / SPLAT_NOISE_M;
  const pz = z / SPLAT_NOISE_M;
  const ix = Math.floor(px);
  const iz = Math.floor(pz);
  const fx = px - ix;
  const fz = pz - iz;
  const ux = fx * fx * (3 - 2 * fx);
  const uz = fz * fz * (3 - 2 * fz);
  const a = hash2(ix, iz);
  const b = hash2(ix + 1, iz);
  const c = hash2(ix, iz + 1);
  const d = hash2(ix + 1, iz + 1);
  return (a + (b - a) * ux) * (1 - uz) + (c + (d - c) * ux) * uz;
}

/** The four layer weights, summing to 1. Channel order is always R, G, B, A. */
export interface SplatWeights {
  grass: number;
  dirt: number;
  rock: number;
  sand: number;
}

/**
 * Which ground a sheet is made of.
 *
 * Both palettes share one contract and one attribute — a `splat` vec4 of four weights summing to
 * 1, in channel order R, G, B, A — so the geometry, the painter and the grass scatter are the
 * same code either way and only the four albedos and the four rules differ.
 *
 * | channel | `'spine'`      | `'downs'`         |
 * | ------- | -------------- | ----------------- |
 * | R       | grass          | bone-pale grass   |
 * | G       | dirt           | dark heath        |
 * | B       | rock           | barrow earth      |
 * | A       | sand           | wet stone         |
 */
export type TerrainPalette = 'spine' | 'downs';

/**
 * Which palette a map's terrain is painted and materialled with. One table rather than a test
 * repeated at four call sites (the real sheet, its grass, the neighbour warm pool, and the region
 * GLB's own terrain mesh) — and getting it wrong is not cosmetic twice over: `downsMaterial` is a
 * pipeline-distinct material, so warming a neighbour with the wrong one compiles the real one at
 * the gate on screen; and under `'downs'` `paintSplat` adopts a four-component `color` attribute
 * as authored splat weights, which on the Spine's GLB is the regolith vertex tint instead.
 *
 * Lanternfall is flat and paved and has no layers to blend; it takes `'spine'` only so
 * `paintSplat` writes something the shader can read, and `render/region.ts` gives its GLB sheet a
 * plain paving material rather than either rule.
 */
export function terrainPaletteFor(mapId: string): TerrainPalette {
  return mapId === 'vigil-downs' ? 'downs' : 'spine';
}

/**
 * **The** splat formula — the one place the rule "steep is rock, low and flat is sand, mid is
 * grass, everything between is dirt" is written down. Both callers go through here: the vertex
 * painter below, and `grassDensityAt` for the grass scatter. Written twice, grass grows on rock.
 *
 * @param heightNorm 0 at the terrain's lowest point, 1 at its highest.
 * @param slope      `1 - normal.y`: 0 on flat ground, 1 on a vertical face.
 * @param noise      0..1 low-frequency mask, for transitions that aren't contour lines.
 */
export function splatWeights(heightNorm: number, slope: number, noise: number): SplatWeights {
  // Rock first and unconditionally: nothing else grows on a face this steep.
  const rock = sstep(ROCK_SLOPE_LO, ROCK_SLOPE_HI, slope + (noise - 0.5) * 0.1);
  const rest = 1 - rock;
  // Sand fills the basin, and only where the ground is also flat — a steep low face is rock.
  const sand =
    rest *
    sstep(SAND_H_HI, SAND_H_LO, heightNorm + (noise - 0.5) * 0.12) *
    (1 - sstep(0.1, 0.3, slope));
  // Grass takes the mid band, thinning out toward the ridges and where the mask says bare.
  const grass =
    (rest - sand) *
    sstep(SAND_H_LO, SAND_H_HI, heightNorm) *
    sstep(1, GRASS_H_HI, heightNorm) *
    sstep(0.25, 0.65, noise);
  // Dirt is the remainder, which is exactly what makes it read as the transition layer.
  const dirt = Math.max(0, rest - sand - grass);
  const sum = rock + sand + grass + dirt || 1;
  return { grass: grass / sum, dirt: dirt / sum, rock: rock / sum, sand: sand / sum };
}

// ---------------------------------------------------------------------------------------------
// The Downs.
//
// Bonelight's ground. Same four-weight splat, different four layers and different rules: the
// Vigil Downs is chalk downland, not a fused-regolith yard, so "steep is rock, low is sand" is
// replaced by "the wind decides". Barrows and cart tracks wear the turf back to earth on the
// exposed tops, heath holds the sheltered hollows, and wet stone shows wherever the chalk breaks
// through a slope.
//
// The layer albedos here are the *fallback*. Each one is a tileable generated texture, and the
// base hex is what the layer reads as if that fetch fails — which is the same fail-soft contract
// `loadTiledTexture` gives the hull and the crates, moved into a uniform so it can resolve after
// the material is already compiled and on screen.
// ---------------------------------------------------------------------------------------------

/** R: bone-pale grass — the Downs' dominant surface, the colour the zone is named for. */
const DOWNS_GRASS = 0xd8d3bc;
/** G: dark heath — the value anchor. Without it the whole zone sits at one lightness. */
const DOWNS_HEATH = 0x78826f;
/** B: barrow earth — turned chalk-and-loam on the mounds and the tracks between them. */
const DOWNS_BARROW = 0xa8a290;
/** A: wet stone — the sage-grey of the ridge line, the same family as the sky's fog. */
const DOWNS_STONE = 0x93a199;

const DOWNS_TEXTURES = [
  '/assets/gen/textures/downs-grass.webp',
  '/assets/gen/textures/downs-heath.webp',
  '/assets/gen/textures/downs-barrow.webp',
  '/assets/gen/textures/downs-stone.webp',
] as const;

/** Per-layer roughness. Turf and heath are matte; wet stone is the only thing here that isn't. */
const DOWNS_ROUGH_GRASS = 0.93;
const DOWNS_ROUGH_HEATH = 0.96;
const DOWNS_ROUGH_BARROW = 0.9;
const DOWNS_ROUGH_STONE = 0.6;

/**
 * Tile size in metres, shared by all four layers.
 *
 * One number, not four, because texel density is a Low-Poly-Bible rule: a stone layer tiled twice
 * as tight as the turf next to it reads as two different games meeting at the splat boundary. It
 * is also one `uv` node feeding four samples rather than four independent ones.
 */
const DOWNS_TILE_M = 4;

/**
 * Distance mip bias, the same shape `scene.ts`'s `triplanarColor` uses and for the same reason:
 * four tiled albedos on a 500 m sheet, seen at a grazing angle, alias past what 16x anisotropy
 * covers and turn the far half of the zone into crawling static (WEBGPU-NOTES §3).
 */
const DOWNS_MIP_NEAR = 40;
const DOWNS_MIP_FAR = 160;
const DOWNS_MIP_BIAS = 2;

/** Slope (`1 - n.y`) over which turf gives out and the chalk shows through as wet stone. */
const DOWNS_STONE_SLOPE_LO = 0.22;
const DOWNS_STONE_SLOPE_HI = 0.6;
/** Normalised height above which the wind scours the tops back to barrow earth. */
const DOWNS_BARROW_H_LO = 0.62;
const DOWNS_BARROW_H_HI = 0.95;
/** Normalised height below which the sheltered hollows hold dark heath. */
const DOWNS_HEATH_H_LO = 0.1;
const DOWNS_HEATH_H_HI = 0.45;

/**
 * The Downs' splat rule, the counterpart to `splatWeights` and deliberately the same shape: one
 * unconditional layer first, then two carved out of the remainder, then the dominant surface as
 * whatever is left. Writing it as "grass is the remainder" is what makes bone-pale turf read as
 * the ground of the zone rather than as one more patch competing with three others.
 *
 * @param heightNorm 0 at the terrain's lowest point, 1 at its highest.
 * @param slope      `1 - normal.y`: 0 on flat ground, 1 on a vertical face.
 * @param noise      0..1 low-frequency mask, so no transition is a contour line.
 */
export function downsSplatWeights(heightNorm: number, slope: number, noise: number): SplatWeights {
  // Stone first and unconditionally: a face this steep is broken chalk whatever is above it.
  const stone = sstep(DOWNS_STONE_SLOPE_LO, DOWNS_STONE_SLOPE_HI, slope + (noise - 0.5) * 0.12);
  const rest = 1 - stone;
  // Barrow earth on the exposed tops and the mound flanks — worn, not grown. The slope term is
  // an *encouragement* here rather than a gate: a barrow is a low dome, so its earth shows on a
  // gentle rise, which is exactly the slope band the stone layer has already declined to take.
  const barrow =
    rest *
    sstep(DOWNS_BARROW_H_LO, DOWNS_BARROW_H_HI, heightNorm + (noise - 0.5) * 0.14) *
    sstep(0.15, 0.5, noise + slope * 0.6);
  // Heath in the sheltered low ground, thinning out as the land rises.
  const heath =
    (rest - barrow) *
    sstep(DOWNS_HEATH_H_HI, DOWNS_HEATH_H_LO, heightNorm) *
    sstep(0.2, 0.6, noise);
  const grass = Math.max(0, rest - barrow - heath);
  const sum = stone + barrow + heath + grass || 1;
  return { grass: grass / sum, dirt: heath / sum, rock: barrow / sum, sand: stone / sum };
}

/** The splat rule for a palette. One lookup so the painter and the grass scatter cannot diverge. */
function weightsFor(palette: TerrainPalette): typeof splatWeights {
  return palette === 'downs' ? downsSplatWeights : splatWeights;
}

/**
 * Texture cache local to this module.
 *
 * Deliberately not `scene.ts`'s `loadTiledTexture`: scene.ts already imports this file, so
 * importing it back is a cycle, and — the substantive reason — this loader needs an `onLoad`
 * callback that `loadTiledTexture` does not expose. A terrain material is compiled long before
 * four albedos have come down the wire, and the only recompile-free way to switch a layer from
 * its flat fallback to its texture afterwards is a uniform the load callback writes.
 */
const downsTextures = new Map<string, { tex: Texture; ready: AnyNode }>();
function downsLayerTexture(path: string): { tex: Texture; ready: AnyNode } {
  const cached = downsTextures.get(path);
  if (cached) return cached;
  const ready = uniform(0) as unknown as AnyNode;
  const tex = new TextureLoader().load(
    path,
    () => {
      ready.value = 1;
    },
    undefined,
    (err) => {
      console.warn(`[stillsun] downs layer failed to load: ${path}; layer stays flat`, err);
    },
  );
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.colorSpace = SRGBColorSpace;
  // Ground seen from a 1.6 m eye is the most foreshortened surface in the game.
  tex.anisotropy = 16;
  const entry = { tex, ready };
  downsTextures.set(path, entry);
  return entry;
}

/**
 * The Vigil Downs terrain material: four tiled albedos blended by the same `splat` attribute the
 * regolith material reads, so the two are interchangeable on any sheet `paintSplat` has painted.
 *
 * Three things are worth knowing before touching it.
 *
 * - **One pipeline for the life of the session.** The texture/flat decision is made here, at
 *   build time, from `low` and `renderFlags.textures` — both fixed at boot. Nothing in this
 *   material varies with the time of day: the day/night ramp moves the *lights*, and a lit
 *   material's response to that is uniforms three already owns. There is no second material for
 *   night and no `needsUpdate` anywhere in this file.
 * - **Fail-soft is a uniform, not a rebuild.** Each layer is `mix(base, sample, ready)` where
 *   `ready` is 0 until that file lands. A missing or 404'd albedo leaves its layer at the flat
 *   base colour named above and every other layer textured; nothing throws, nothing recompiles,
 *   and the zone is still readable.
 * - **The Spine's `regolithMaterial` is untouched.** This is a sibling, not a replacement, and
 *   `render/materials.ts`'s `upgradeRegolithMaterial` is deliberately *not* applied to it — the
 *   sparkle, the opposition surge and the grain normal are a fused-regolith story and chalk
 *   downland has none of them.
 *
 * @param low Low tier drops the four texture fetches and the tonal octave and runs the flat
 *            four-colour blend, which is four multiply-adds and no sampler.
 */
export function downsMaterial(low: boolean): MeshStandardNodeMaterial {
  const mat = new MeshStandardNodeMaterial({ roughness: 0.92, metalness: 0 });
  mat.name = 'downs-terrain';
  const textured = renderFlags.textures && !low;
  try {
    const w = attribute('splat', 'vec4') as unknown as AnyNode;

    const bases = [DOWNS_GRASS, DOWNS_HEATH, DOWNS_BARROW, DOWNS_STONE];
    const layers: AnyNode[] = bases.map((hex) => color(hex));
    if (textured) {
      // One shared planar UV: the Downs is a heightfield, so its normal is within ~30° of +Y
      // nearly everywhere and a triplanar blend would buy three samples per layer to fix the
      // handful of pixels on a chalk face. The mip bias is what actually keeps the far half of
      // the zone from aliasing, and it costs one smoothstep for all four layers.
      const uv = positionWorld.xz.div(DOWNS_TILE_M);
      const bias = smoothstep(DOWNS_MIP_NEAR, DOWNS_MIP_FAR, positionView.length()).mul(
        DOWNS_MIP_BIAS,
      );
      for (let i = 0; i < layers.length; i++) {
        const path = DOWNS_TEXTURES[i];
        const base = layers[i];
        if (path === undefined || base === undefined) continue;
        const { tex, ready } = downsLayerTexture(path);
        layers[i] = mix(base, texture(tex, uv).bias(bias).rgb, ready);
      }
    }

    const [gr, he, ba, st] = layers as [AnyNode, AnyNode, AnyNode, AnyNode];
    let surface: AnyNode = gr.mul(w.x).add(he.mul(w.y)).add(ba.mul(w.z)).add(st.mul(w.w));
    let rough: AnyNode = float(DOWNS_ROUGH_GRASS)
      .mul(w.x)
      .add(float(DOWNS_ROUGH_HEATH).mul(w.y))
      .add(float(DOWNS_ROUGH_BARROW).mul(w.z))
      .add(float(DOWNS_ROUGH_STONE).mul(w.w));

    // The same ~25 m tonal octave the Spine gets, at half the amplitude: Bonelight is a
    // high-key, low-contrast palette and 8% of value swing on bone-pale turf reads as dirt.
    if (renderFlags.noise && !low) {
      const tone = mx_noise_float(positionWorld.xz.mul(1 / 25)).mul(0.04);
      surface = surface.mul(float(1).add(tone));
    }

    // Steep faces read cooler and darker here, not lighter — the inverse of the Spine's rule,
    // and the reason is the palette rather than the physics. Under a bone-white sky the danger
    // is that near and far hills sit at the same value (the failure written on Option C's own
    // board); pulling every slope toward the ridge colour is what puts a legible dark edge on
    // the underside of a barrow and keeps the Downs from flattening into one sheet of paper.
    surface = mix(
      surface,
      color(DOWNS_STONE),
      smoothstep(0.3, 0.8, normalWorld.y.clamp(0, 1).oneMinus()).mul(0.18),
    );

    mat.colorNode = surface;
    mat.roughnessNode = rough;
  } catch (err) {
    console.warn('[stillsun] TSL downs shading unavailable, using flat material', err);
    mat.color.setHex(DOWNS_GRASS);
  }
  return mat;
}

/** Cached per-heightfield min/max, so `grassDensityAt` doesn't walk `heights` per blade. */
const heightRanges = new WeakMap<Heightfield, { min: number; max: number; flat: boolean }>();
function heightRange(hf: Heightfield): { min: number; max: number; flat: boolean } {
  let range = heightRanges.get(hf);
  if (range) return range;
  let min = Infinity;
  let max = -Infinity;
  for (const h of hf.heights) {
    if (h < min) min = h;
    if (h > max) max = h;
  }
  if (!Number.isFinite(min)) {
    min = 0;
    max = 1;
  }
  // `max > min ? max : min + 1` is the divide-by-zero guard, and it has to stay: Lanternfall's
  // heightfield is all zeros on purpose (the city is flat, every step there is a collider box),
  // and it exists only so the client's terrain branch runs and a region GLB has something to
  // attach to. Without the widen, `(h - min) / (max - min)` is 0/0 on that map and every splat
  // weight downstream is NaN. `flat` records that the span is synthetic, so a caller can tell
  // "the lowest point of a real hillside" from "there is no hillside".
  const flat = !(max > min);
  range = { min, max: max > min ? max : min + 1, flat };
  heightRanges.set(hf, range);
  return range;
}

/** Grid-clamped height lookup, the same indexing `heightAt()` in `packages/shared` uses. */
function gridHeight(hf: Heightfield, c: number, r: number): number {
  const cc = Math.min(Math.max(c, 0), Math.max(0, hf.cols - 1));
  const rr = Math.min(Math.max(r, 0), Math.max(0, hf.rows - 1));
  return hf.heights[rr * hf.cols + cc] ?? 0;
}

/**
 * Slope at a world XZ, from the heightfield's own central differences rather than from an
 * interpolated vertex normal: the two disagree at ridges, and a grass scatter that used the
 * smoothed one would plant blades on a cliff edge the terrain paints as rock.
 */
function slopeAt(hf: Heightfield, x: number, z: number): number {
  const cell = hf.cellSize > 0 ? hf.cellSize : 1;
  const c = Math.round((x - hf.originX) / cell);
  const r = Math.round((z - hf.originZ) / cell);
  const dx = (gridHeight(hf, c + 1, r) - gridHeight(hf, c - 1, r)) / (2 * cell);
  const dz = (gridHeight(hf, c, r + 1) - gridHeight(hf, c, r - 1)) / (2 * cell);
  return 1 - 1 / Math.sqrt(1 + dx * dx + dz * dz);
}

/**
 * The grass layer's weight at a world XZ — the scatter mask `render/grass.ts` takes as its
 * `density`. Same formula the terrain paints itself with, so a blade never stands on sand.
 */
export function grassDensityAt(
  hf: Heightfield,
  x: number,
  z: number,
  palette: TerrainPalette = 'spine',
): number {
  const { min, max, flat } = heightRange(hf);
  // A heightfield with no relief is a placeholder, not a meadow. Lanternfall's is all zeros so
  // the terrain branch runs under a region GLB; scattering blades across its paved plaza would
  // be the wrong read whatever the splat rule happened to return. Short-circuiting here also
  // makes that *explicit*: both rules already return grass 0 at `heightNorm` 0 with zero slope
  // (the spine's sand basin and the downs' heath hollow each claim the whole sheet), so this
  // changes no behaviour today — it just stops the right answer depending on an accident of two
  // smoothstep windows that somebody will later retune.
  if (flat) return 0;
  const cell = hf.cellSize > 0 ? hf.cellSize : 1;
  const c = Math.round((x - hf.originX) / cell);
  const r = Math.round((z - hf.originZ) / cell);
  const heightNorm = (gridHeight(hf, c, r) - min) / (max - min);
  return weightsFor(palette)(heightNorm, slopeAt(hf, x, z), lowNoise(x, z)).grass;
}

/**
 * Paints the `splat` attribute onto a terrain geometry from its own positions and normals, and
 * returns it. Works for any terrain sheet — the heightfield mesh below and the Blender region
 * GLB (render/region.ts) both go through it — because it needs nothing but `position` and
 * `normal`. Call it *after* `computeVertexNormals()`; a geometry reaching a terrain material
 * without it will not build a shader.
 *
 * Repainting after a map change is exactly this call on the new geometry: the weights live in
 * the mesh, not in the material, so the material is never rebuilt and never recompiles.
 *
 * **Under `'downs'` only, an authored splat wins.** An existing `splat` attribute is left alone,
 * and a four-component `color` attribute is *adopted* into `splat` — which is the contract the
 * Downs' Blender export is written against: `tools/assetgen/blender/downs_region.py` paints the
 * four weights as vertex colours (R grass, G heath, B barrow earth, A wet stone), glTF carries
 * them as `COLOR_0`, three loads that as `color`, and this call moves them across. Authored
 * weights always beat re-derived ones: the exporter knows where the barrows and the cart tracks
 * are and a height/slope rule can only guess.
 *
 * That adoption is gated on the palette on purpose. The Spine's region GLB also carries a
 * `color` attribute and it means something completely different there — it is `regolithMaterial`'s
 * `vertexTint`, a zone paint with gloss in alpha — so reading it as weights would repaint the
 * whole Spine with nonsense. `'spine'` behaviour is exactly what it always was.
 */
export function paintSplat(
  geometry: BufferGeometry,
  palette: TerrainPalette = 'spine',
): BufferGeometry {
  const authored = palette === 'downs' ? geometry.getAttribute('color') : undefined;
  if (palette === 'downs' && geometry.getAttribute('splat') !== undefined) return geometry;
  if (authored !== undefined && authored.itemSize === 4) {
    const src = authored;
    const out4 = new Float32Array(src.count * 4);
    for (let i = 0; i < src.count; i++) {
      // Renormalised on the way in: a painted weight set that does not sum to 1 would brighten
      // or darken the ground as a whole, and vertex-colour round-tripping through 8-bit glTF
      // does not preserve a sum exactly.
      const r = src.getX(i);
      const g = src.getY(i);
      const b = src.getZ(i);
      const a = src.getW(i);
      const s = r + g + b + a || 1;
      out4[i * 4] = r / s;
      out4[i * 4 + 1] = g / s;
      out4[i * 4 + 2] = b / s;
      out4[i * 4 + 3] = a / s;
    }
    geometry.setAttribute('splat', new BufferAttribute(out4, 4));
    return geometry;
  }
  const rule = weightsFor(palette);
  const pos = geometry.getAttribute('position');
  const nrm = geometry.getAttribute('normal');
  const count = pos.count;
  const out = new Float32Array(count * 4);
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < count; i++) {
    const y = pos.getY(i);
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const span = maxY > minY ? maxY - minY : 1;
  for (let i = 0; i < count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const slope = nrm === undefined ? 0 : 1 - Math.min(1, Math.max(0, nrm.getY(i)));
    const w = rule((pos.getY(i) - minY) / span, slope, lowNoise(x, z));
    out[i * 4] = w.grass;
    out[i * 4 + 1] = w.dirt;
    out[i * 4 + 2] = w.rock;
    out[i * 4 + 3] = w.sand;
  }
  geometry.setAttribute('splat', new BufferAttribute(out, 4));
  return geometry;
}

export interface TerrainMeshOptions {
  /** Low tier drops the tonal noise octave — it runs per fragment over the whole sheet. */
  low?: boolean;
  /**
   * Which ground the sheet is made of. `'spine'` (the default) is the original regolith splat —
   * grass / dirt / rock / sand, no texture fetch — and is unchanged. `'downs'` is Bonelight's
   * Vigil Downs: bone-pale grass / dark heath / barrow earth / wet stone, tiled from four
   * generated albedos. See `downsMaterial`.
   */
  palette?: TerrainPalette;
  /**
   * Multiply the terrain by the mesh's `color` attribute and read its alpha as a gloss mask.
   * The Blender region terrain (render/region.ts) paints its zones this way — a darker basin
   * floor, dust-lit cliff faces, the mirror-flat glass pans on the dayside edge. Off for the
   * plain heightfield mesh, which has no colour attribute.
   */
  vertexTint?: boolean;
}

export interface TerrainBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** World-space XZ bounds of a heightfield's grid, for sizing the shadow frustum and fog. */
export function terrainBounds(hf: Heightfield): TerrainBounds {
  const spanX = Math.max(0, hf.cols - 1) * hf.cellSize;
  const spanZ = Math.max(0, hf.rows - 1) * hf.cellSize;
  return {
    minX: hf.originX,
    maxX: hf.originX + spanX,
    minZ: hf.originZ,
    maxZ: hf.originZ + spanZ,
  };
}

/**
 * The terrain material: four albedo layers blended by the `splat` vertex attribute, plus one
 * low-frequency tonal octave so a 500 m zone doesn't read as four flat cards. One material, one
 * draw, and — the point of the rewrite — a fragment stage of four multiply-adds where the old
 * regolith stack ran five `mx_noise_float` octaves, two texture samples with a distance mip
 * bias, and a surface-gradient normal perturbation over every ground pixel on screen.
 *
 * **The geometry must carry a `splat` attribute** — call `paintSplat(geometry)` on it first.
 * Both terrain paths in this codebase do (`buildTerrainMesh` below, and region.ts's GLB sheet).
 *
 * `render/materials.ts`'s `upgradeRegolithMaterial` still layers the sun glints, the opposition
 * effect and the near-field grain normal on top of whatever this leaves in `colorNode` /
 * `roughnessNode` / `normalNode`, so the surface response the AD brief asks for is unchanged.
 */
export function regolithMaterial(low: boolean, vertexTint = false): MeshStandardNodeMaterial {
  const mat = new MeshStandardNodeMaterial({ roughness: 0.95, metalness: 0 });
  try {
    // Painted on the CPU and already normalised to sum 1, so this is a partition of the surface
    // and the blend can never brighten or darken it as a whole.
    const w = attribute('splat', 'vec4') as unknown as AnyNode;
    let surface: AnyNode = color(LAYER_GRASS)
      .mul(w.x)
      .add(color(LAYER_DIRT).mul(w.y))
      .add(color(LAYER_ROCK).mul(w.z))
      .add(color(LAYER_SAND).mul(w.w));
    let rough: AnyNode = float(ROUGH_GRASS)
      .mul(w.x)
      .add(float(ROUGH_DIRT).mul(w.y))
      .add(float(ROUGH_ROCK).mul(w.z))
      .add(float(ROUGH_SAND).mul(w.w));

    // One octave, ~25 m, +/-8% on value only. Enough to stop a hillside being one flat colour;
    // far too coarse to alias, which is what killed the old fine octaves at range. Skipped on
    // the low tier and under `?noise=0`.
    if (renderFlags.noise && !low) {
      const tone = mx_noise_float(positionWorld.xz.mul(1 / 25)).mul(0.08);
      surface = surface.mul(float(1).add(tone));
    }

    // Steep faces catch the grazing sun and read lighter whatever they are made of — the same
    // cue the AD brief uses for the glass field, kept subtle because at a 4.5° elevation the
    // lighting term already swings hard with slope and a strong albedo tint double-counts it.
    surface = mix(
      surface,
      color(LAYER_SAND),
      smoothstep(0.25, 0.75, normalWorld.y.clamp(0, 1).oneMinus()).mul(0.1),
    );

    if (vertexTint) {
      // Zone paint from the region GLB (authored as linear multipliers around 1, alpha = gloss).
      const tint = vertexColor();
      surface = surface.mul(tint.rgb);
      // Fused regolith is a mirror at a grazing sun: the gloss mask pulls roughness right down
      // so the glass pans catch the Still the way the AD brief describes the dayside edge.
      rough = mix(rough, float(0.22), tint.a);
    }
    mat.colorNode = surface;
    mat.roughnessNode = rough;
  } catch (err) {
    console.warn('[stillsun] TSL terrain shading unavailable, using flat material', err);
    mat.color.setHex(LAYER_DIRT);
    if (vertexTint) mat.vertexColors = true;
  }
  return mat;
}

/**
 * Builds a terrain mesh from a shared-sim heightfield. Vertices sit exactly on the sim's
 * grid (same `originX`/`originZ`/`cellSize`/`heights` indexing as `heightAt()` in
 * `packages/shared/src/terrain.ts`), so a standing player's feet never float or sink
 * relative to what the client renders — the sim's bilinear sample at any grid corner
 * equals that corner's own height exactly.
 */
export function buildTerrainMesh(hf: Heightfield, opts: TerrainMeshOptions = {}): Mesh {
  const { cols, rows, cellSize, originX, originZ, heights } = hf;
  const safeCols = Math.max(2, cols);
  const safeRows = Math.max(2, rows);
  const vertCount = safeCols * safeRows;

  const positions = new Float32Array(vertCount * 3);
  const uvs = new Float32Array(vertCount * 2);
  for (let r = 0; r < safeRows; r++) {
    for (let c = 0; c < safeCols; c++) {
      const i = r * safeCols + c;
      positions[i * 3] = originX + c * cellSize;
      positions[i * 3 + 1] = heights[i] ?? 0;
      positions[i * 3 + 2] = originZ + r * cellSize;
      uvs[i * 2] = c / (safeCols - 1);
      uvs[i * 2 + 1] = r / (safeRows - 1);
    }
  }

  const quadsX = safeCols - 1;
  const quadsZ = safeRows - 1;
  const indices = new Uint32Array(quadsX * quadsZ * 6);
  let idx = 0;
  for (let r = 0; r < quadsZ; r++) {
    for (let c = 0; c < quadsX; c++) {
      const a = r * safeCols + c;
      const b = a + 1;
      const d0 = a + safeCols;
      const d1 = d0 + 1;
      // Winding verified against BufferGeometry.computeVertexNormals's own
      // cross(pC - pB, pA - pB): this order yields a +Y-up normal on flat ground.
      indices[idx++] = a;
      indices[idx++] = d0;
      indices[idx++] = b;
      indices[idx++] = b;
      indices[idx++] = d0;
      indices[idx++] = d1;
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(new BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  // After the normals: the splat's rock/stone layer is driven by slope, which is read off them.
  paintSplat(geometry, opts.palette ?? 'spine');
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  const low = opts.low ?? false;
  const mesh = new Mesh(
    geometry,
    (opts.palette ?? 'spine') === 'downs'
      ? downsMaterial(low)
      : regolithMaterial(low, opts.vertexTint ?? false),
  );
  mesh.name = 'terrain';
  mesh.receiveShadow = true;
  mesh.castShadow = false;
  return mesh;
}
